import { TypeSystem } from "../../support/type-system.support";
import { CampaignProductReplacement } from "./campaign-product-replacement.model";
import { CampaignProductSuggested } from "./campaign-product-suggested.model";
import { Campaign } from "./campaign.model";
import { PaymentPlan } from "./payment-plan.enum";
import { Product } from "../circular-dto/product.conflict";
import { Reach } from "./reach.model";

    /**
     * @description
     *  Modelo de un producto de campaña.
     *
     * @see gigot-backend/Models/Products/CampaignProduct.cs
     */

export class CampaignProduct {

	public campaign: Campaign;
	public campaignId: number;
	public campaignProductFoxCategoryId: number;
	public campaignProductReplacements: CampaignProductReplacement[];
	public campaignProductSuggested: CampaignProductSuggested[];
	public estimatedSales: number;
	public id: number;
	public maxOrder: number;
	public paymentCycles: number;
	public paymentPlan: PaymentPlan;
	public points: number;
	public previousCampaign: boolean;
	public price: number;
	public product: Product;
	public productId: number;
	public productTypeId: number;
	public reach: Reach;
	public reachId: number;

    /**
     * @description
     *	Construye un producto de campaña con una semántica vacía. Nótese que
	 *	no es lo mismo que construir un producto con valores por defecto. Las
	 *	reglas generales de construcción son:
     *
     *      - Campos 'array' se definen como [].
     *      - Campos 'boolean' se definen como 'false'.
     *      - Campos 'Date' se definen como 'undefined'.
     *      - Campos 'enum' se definen como 'undefined'.
     *      - Campos 'number' se definen como 'undefined'.
     *      - Campos 'string' se definen como '' (cadena vacía).
     *      - Campos 'object' se definen llamando a su propio método #empty().
     *      - Campos de la superclase se definen al igual que campos 'object'.
     *      - Campos genéricos se definen como 'undefined'.
	 *		- Campos recursivos se definen como 'undefined'.
     *
     * @returns
     *	Un producto de campaña vacío.
     */
    public static empty(): CampaignProduct {
        const campaignProduct: CampaignProduct = new CampaignProduct();
		campaignProduct.campaign = Campaign.empty();
		campaignProduct.campaignId = undefined;
		campaignProduct.campaignProductFoxCategoryId = undefined;
		campaignProduct.campaignProductReplacements = [];
		campaignProduct.campaignProductSuggested = [];
		campaignProduct.estimatedSales = undefined;
		campaignProduct.id = undefined;
		campaignProduct.maxOrder = undefined;
		campaignProduct.paymentCycles = undefined;
		campaignProduct.paymentPlan = undefined;
		campaignProduct.points = undefined;
		campaignProduct.previousCampaign = false;
		campaignProduct.price = undefined;
		campaignProduct.product = Product.empty();
		campaignProduct.productId = undefined;
		campaignProduct.productTypeId = undefined;
		campaignProduct.reach = Reach.empty();
		campaignProduct.reachId = undefined;
        return campaignProduct;
    }

	/**
     * @description
     *  Método de reificación de un modelo compatible anémico. Esto permite
     *  ejecutar lógica de negocios sobre el modelo generado, siendo que el
     *  valor de sus propiedades se mantiene.
     *
     * @param anemicCampaignProduct
     *  El modelo anémico a reificar.
     *
     * @returns
     *  Devuelve un modelo de esta clase, cuyas propiedades fueron copiadas en
     *  profundidad desde el modelo anémico.
     */
    public static from(anemicCampaignProduct: CampaignProduct): CampaignProduct {
		if (!anemicCampaignProduct) return undefined;
        const campaignProduct: CampaignProduct = CampaignProduct.empty();
		campaignProduct.campaign = Campaign.from(anemicCampaignProduct.campaign);
		campaignProduct.campaignId = anemicCampaignProduct.campaignId;
		campaignProduct.campaignProductFoxCategoryId = anemicCampaignProduct.campaignProductFoxCategoryId;
		campaignProduct.campaignProductReplacements = TypeSystem.orElse(anemicCampaignProduct.campaignProductReplacements, [])
			.map(campaignProductReplacement => CampaignProductReplacement.from(campaignProductReplacement));
		campaignProduct.campaignProductSuggested = TypeSystem.orElse(anemicCampaignProduct.campaignProductSuggested, [])
			.map(campaignProductSuggested => CampaignProductSuggested.from(campaignProductSuggested));
		campaignProduct.estimatedSales = anemicCampaignProduct.estimatedSales;
		campaignProduct.id = anemicCampaignProduct.id;
		campaignProduct.maxOrder = anemicCampaignProduct.maxOrder;
		campaignProduct.paymentCycles = anemicCampaignProduct.paymentCycles;
		campaignProduct.paymentPlan = anemicCampaignProduct.paymentPlan;
		campaignProduct.points = anemicCampaignProduct.points;
		campaignProduct.previousCampaign = anemicCampaignProduct.previousCampaign;
		campaignProduct.price = anemicCampaignProduct.price;
		campaignProduct.product = Product.from(anemicCampaignProduct.product);
		campaignProduct.productId = anemicCampaignProduct.productId;
		campaignProduct.productTypeId = anemicCampaignProduct.productTypeId;
		campaignProduct.reach = Reach.from(anemicCampaignProduct.reach);
		campaignProduct.reachId = anemicCampaignProduct.reachId;
        return campaignProduct;
    }
}
